import { useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { TextInput } from '@forwardflow/ui-v2'
import { PhoneIcon, SearchMagnifyingGlassIcon } from '@forwardflow/icons'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useSearchAvailableAreaCodes } from '@forwardflow/queries/onboarding.queries'
import { DEV_DUMMY, isFreeFlow, isSandbox, useDevTools } from '../dev/devTools'
import { Card, LinkButton, Section, Skeleton, StepFooter, StepHeader } from '../shell/Card'
import { MAX_AREA_CODES, useAreaCodeSuggestions } from './area-code/suggestions'

/** Chips for the team's codes and a search box; writes the selection back. */
export const AreaCodePicker = ({
   selectedCodes,
   onChange,
}: {
   selectedCodes: string[]
   onChange: (codes: string[]) => void
}) => {
   const { suggestions, isSuggesting } = useAreaCodeSuggestions()
   const [codeSearch, setCodeSearch] = useState('')
   const searched = codeSearch.replace(/\D/g, '').slice(0, 3)
   const { data: searchedCodes, isLoading: isSearching } = useSearchAvailableAreaCodes(
      searched.length === 3 && !isSandbox() ? searched : ''
   )
   const searchResults: string[] = isSandbox()
      ? searched.length === 3
         ? [searched]
         : []
      : (searchedCodes?.areaCodes || []).map(String)

   const toggleCode = (code: string) => {
      if (selectedCodes.includes(code)) onChange(selectedCodes.filter((c) => c !== code))
      else if (selectedCodes.length < MAX_AREA_CODES) onChange([...selectedCodes, code])
   }

   const codeChip = (code: string, extra?: string, unavailable?: boolean) => {
      const isSelected = selectedCodes.includes(code)
      const full = !isSelected && selectedCodes.length >= MAX_AREA_CODES
      return (
         <button
            key={code}
            type="button"
            disabled={full || unavailable}
            title={unavailable ? 'No numbers available in this area code right now' : undefined}
            onClick={() => toggleCode(code)}
            data-testid={`code-${code}`}
            className={cn(
               'rounded-lg border px-3.5 py-2 text-sm transition-all',
               isSelected
                  ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.30)] text-white'
                  : full || unavailable
                    ? 'border-white/10 bg-white/5 text-white/30 cursor-not-allowed'
                    : 'border-white/20 bg-white/5 text-white hover:border-[#9F7BFF]/50 hover:bg-[rgba(159,123,255,0.10)]'
            )}
         >
            ({code})
            {extra && <span className="text-white/50 ml-2 text-xs">{extra}</span>}
            {unavailable && <span className="text-white/40 ml-2 text-xs">unavailable</span>}
         </button>
      )
   }

   return (
      <div className="space-y-4" data-testid="area-code-editor">
         <Section
            label="From your team's phone numbers"
            hint="Tap to select. Up to three area codes; the first one is used most."
         >
            {isSuggesting ? (
               <div className="flex gap-2">
                  <Skeleton className="h-9 w-24" />
                  <Skeleton className="h-9 w-24" />
               </div>
            ) : suggestions.length || selectedCodes.length ? (
               <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) =>
                     codeChip(s.code, s.isOwner ? 'your number' : s.name, s.available === false)
                  )}
                  {/* Codes picked by search stay visible so they can be removed. */}
                  {selectedCodes
                     .filter((c) => !suggestions.some((s) => s.code === c))
                     .map((c) => codeChip(c, 'you added'))}
               </div>
            ) : (
               <p className="text-white/40 text-sm">No phone numbers on your Follow Up Boss team yet.</p>
            )}
         </Section>
         <TextInput
            size="sm"
            label="Or any other area code"
            leftSection={<SearchMagnifyingGlassIcon width={16} />}
            placeholder="3 digits, e.g. 616"
            value={codeSearch}
            onChange={(e) => setCodeSearch(e.currentTarget.value.replace(/\D/g, '').slice(0, 3))}
            inputMode="numeric"
            maxLength={3}
            data-testid="area-code-search"
         />
         {searched.length === 3 && (
            <div className="flex flex-wrap gap-2">
               {isSearching ? (
                  <Skeleton className="h-9 w-24" />
               ) : searchResults.length ? (
                  searchResults.map((c) => codeChip(c))
               ) : (
                  <p className="text-white/40 text-sm">
                     No numbers available in ({searched}) right now. Try a nearby one.
                  </p>
               )}
            </div>
         )}
      </div>
   )
}

/**
 * Step 4: the area code leads see.
 *
 * Taken from the owner's phone (then the team's), availability-checked and
 * pre-selected. "Change" opens the picker. The numbers themselves are
 * bought when a campaign launches; the customer never has to pick numbers.
 */
export const AreaCode = () => {
   const { onboardingData, getNextStep, getPreviousStep, setCampaignConfiguration } =
      useOnboardingStoreV2()
   const freeFlow = isFreeFlow()
   const selectedCodes = useMemo(
      () => onboardingData.campaignConfiguration?.area_codes || [],
      [onboardingData.campaignConfiguration?.area_codes]
   )
   const { isSuggesting, ready, ownerCode } = useAreaCodeSuggestions()

   // Pre-select the owner's code once, if nothing is chosen yet.
   const preselected = useRef(false)
   useEffect(() => {
      if (preselected.current || isSuggesting || selectedCodes.length) return
      if (!ready.length) return
      preselected.current = true
      setCampaignConfiguration({ area_codes: [ready[0]] })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isSuggesting, ready.join(',')])

   const [changing, setChanging] = useState(false)
   const primary = selectedCodes[0]
   const matchesOwner = primary && primary === ownerCode

   useDevTools({
      fill: () => setCampaignConfiguration({ area_codes: DEV_DUMMY.areaCodes.slice(0, 1) }),
      skip: () => {
         if (!selectedCodes.length)
            setCampaignConfiguration({ area_codes: DEV_DUMMY.areaCodes.slice(0, 1) })
         getNextStep()
         return true
      },
   })

   const canContinue = freeFlow || selectedCodes.length > 0

   return (
      <div className="space-y-4">
         <Card testId="number-card">
            <StepHeader
               title="Your leads will see calls and texts from this area code"
               lede="We buy phone numbers with this area code and use them to call and text your leads. A local number gets picked up far more often than an out-of-town one."
            />

            <div className="rounded-xl border border-[#9F7BFF] bg-[rgba(159,123,255,0.10)] p-4 flex items-center gap-4">
               <span className="flex shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-[#9F7BFF]/20 text-[#A684FF]">
                  <PhoneIcon width={22} height={22} />
               </span>
               <div className="flex-1 min-w-0">
                  {isSuggesting && !selectedCodes.length ? (
                     <Skeleton className="h-8 w-40" />
                  ) : selectedCodes.length ? (
                     <p className="text-white font-semibold text-2xl leading-none" data-testid="area-code-value">
                        {selectedCodes.map((c) => `(${c})`).join('  ')}
                     </p>
                  ) : (
                     <p className="text-white font-semibold text-lg">Pick an area code</p>
                  )}
                  <p className="text-[#99A1AF] text-xs mt-1.5">
                     {matchesOwner
                        ? 'Matches your own mobile number, so leads see a caller from their area.'
                        : selectedCodes.length
                          ? 'Leads see a caller from this area.'
                          : 'No phone numbers on your team in Follow Up Boss yet. Search for an area code below.'}
                  </p>
               </div>
               <LinkButton onClick={() => setChanging((v) => !v)} testId="change-area-code">
                  {changing ? 'Done' : 'Change'}
               </LinkButton>
            </div>

            {changing && (
               <div className="border-t border-white/10 pt-4">
                  <AreaCodePicker
                     selectedCodes={selectedCodes}
                     onChange={(codes) => setCampaignConfiguration({ area_codes: codes })}
                  />
               </div>
            )}

            <p className="text-white/40 text-xs">
               Your first three numbers are included. Numbers are bought when your first campaign
               starts, and you can add more area codes later under Phone numbers.
            </p>
         </Card>

         <StepFooter
            onBack={() => getPreviousStep()}
            action={
               selectedCodes.length
                  ? `Confirm ${selectedCodes.map((c) => `(${c})`).join(', ')}`
                  : 'Pick an area code to continue'
            }
            onAction={() => getNextStep()}
            disabled={!canContinue}
            note="Next: the number we hand interested leads to you from."
            testId="confirm-area-code"
         />
      </div>
   )
}
